import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useVoiceDictation — Vague 7.
 *
 * Wraps the browser Web Speech API (`webkitSpeechRecognition` or `SpeechRecognition`)
 * to dictate French text into the note textarea. 100% client-side, zero cost.
 *
 * Compat :
 *   ✅ Chrome desktop + Android
 *   ✅ Edge
 *   ✅ Safari 14.1+ (iOS 14.5+)
 *   ❌ Firefox — falls back gracefully to `isSupported: false`
 *
 * Usage :
 *   const { isSupported, isListening, transcript, start, stop, error, reset }
 *     = useVoiceDictation({ lang: "fr-FR", continuous: true });
 *   → transcript = accumulated final text so far
 *   → onResult(chunk) callback available via `onFinal` prop
 *
 * The hook DOES NOT own the note draft — the caller merges `transcript`
 * (final results) into its own state. Interim results are exposed via
 * `interim` for a live preview if desired.
 */
export function useVoiceDictation({
  lang = "fr-FR",
  continuous = true,
  interimResults = true,
  onFinal = null,
} = {}) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interim, setInterim] = useState("");
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;

  const isSupported = typeof window !== "undefined"
    && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const stop = useCallback(() => {
    const r = recognitionRef.current;
    if (r) {
      try { r.stop(); } catch (_) { /* noop */ }
    }
    setIsListening(false);
  }, []);

  const start = useCallback(() => {
    if (!isSupported) {
      setError("Ton navigateur ne supporte pas la dictée vocale. Utilise Chrome, Edge ou Safari.");
      return;
    }
    setError(null);
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new Rec();
    r.lang = lang;
    r.continuous = continuous;
    r.interimResults = interimResults;

    r.onresult = (e) => {
      let finalChunk = "";
      let interimChunk = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i];
        if (res.isFinal) finalChunk += res[0].transcript;
        else interimChunk += res[0].transcript;
      }
      if (finalChunk) {
        setTranscript((prev) => (prev + " " + finalChunk).trim());
        onFinalRef.current?.(finalChunk.trim());
      }
      setInterim(interimChunk);
    };
    r.onerror = (e) => {
      // Common error codes : "no-speech", "audio-capture", "not-allowed", "network"
      const code = e?.error || "unknown";
      const msg = {
        "no-speech": "Aucune parole détectée — parle plus fort ou plus près du micro.",
        "audio-capture": "Aucun micro détecté sur cet appareil.",
        "not-allowed": "Accès au micro refusé. Autorise-le dans les réglages navigateur.",
        "network": "Erreur réseau — la reconnaissance a besoin d'internet.",
      }[code] || `Erreur reconnaissance : ${code}`;
      setError(msg);
      setIsListening(false);
    };
    r.onend = () => {
      setIsListening(false);
      setInterim("");
    };

    recognitionRef.current = r;
    try {
      r.start();
      setIsListening(true);
    } catch (err) {
      // e.g. calling start() while a session is still active
      setError("Impossible de démarrer la dictée. Réessaie.");
      setIsListening(false);
    }
  }, [lang, continuous, interimResults, isSupported]);

  const reset = useCallback(() => {
    setTranscript("");
    setInterim("");
    setError(null);
  }, []);

  useEffect(() => () => stop(), [stop]);

  return { isSupported: !!isSupported, isListening, transcript, interim, error, start, stop, reset };
}
